import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { API } from '../../lib/img';

const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#111111' };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px', fontFamily: 'var(--font-body)', boxSizing: 'border-box' };

const Field = ({ label, value, onChange, rows }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={labelStyle}>{label}</label>
    {rows ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} style={{ ...inputStyle, resize: 'vertical' }} />
    ) : (
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
    )}
  </div>
);

const ListEditor = ({ label, items, onChange, rows = 2 }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={labelStyle}>{label}</label>
    {(items || []).map((item, i) => (
      <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <textarea
          value={item}
          onChange={(e) => { const next = [...items]; next[i] = e.target.value; onChange(next); }}
          rows={rows}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))}
          style={{ border: 'none', background: 'transparent', color: '#cc0000', cursor: 'pointer', flexShrink: 0 }}>
          <Trash2 size={18} />
        </button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...(items || []), ''])}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px dashed #C9A24B', background: 'transparent', color: '#9A7B2E', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>
      <Plus size={14} /> Add item
    </button>
  </div>
);

const ZonesEditor = ({ zones, onChange }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={labelStyle}>Climate zones (icon stays fixed per card)</label>
    {(zones || []).map((zone, i) => (
      <div key={i} style={{ border: '1px solid #E0E0E0', borderRadius: '6px', padding: '14px', marginBottom: '12px', backgroundColor: '#FAFAF7' }}>
        <input
          type="text"
          value={zone.title || ''}
          onChange={(e) => { const next = zones.map((z, idx) => idx === i ? { ...z, title: e.target.value } : z); onChange(next); }}
          placeholder="Zone title"
          style={{ ...inputStyle, marginBottom: '8px', fontWeight: '600' }}
        />
        <textarea
          value={zone.description || ''}
          onChange={(e) => { const next = zones.map((z, idx) => idx === i ? { ...z, description: e.target.value } : z); onChange(next); }}
          rows={3}
          placeholder="Zone description"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>
    ))}
  </div>
);

const PagesManager = () => {
  const [home, setHome] = useState(null);
  const [book, setBook] = useState(null);
  const [authorsPage, setAuthorsPage] = useState(null);
  const [preorder, setPreorder] = useState(null);
  const [savingHome, setSavingHome] = useState(false);
  const [savingBook, setSavingBook] = useState(false);
  const [savingAuthors, setSavingAuthors] = useState(false);
  const [savingPreorder, setSavingPreorder] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetch(`${API}/api/content/home`).then((r) => r.json()).then((d) => setHome(d.content || {}));
    fetch(`${API}/api/content/about_book`).then((r) => r.json()).then((d) => setBook(d.content || {}));
    fetch(`${API}/api/content/about_authors`).then((r) => r.json()).then((d) => setAuthorsPage(d.content || {}));
    fetch(`${API}/api/content/buy_book`).then((r) => r.json()).then((d) => setPreorder(d.content || {}));
  }, []);

  const save = async (section, content, setSaving) => {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API}/api/admin/content/${section}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      });
      const data = await res.json();
      setMsg(res.ok && data.success ? `Saved "${section}" successfully.` : (data.detail || 'Save failed.'));
    } catch (e) {
      setMsg('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const card = { backgroundColor: '#FFFFFF', padding: '32px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', marginBottom: '24px' };
  const saveBtn = (saving) => ({ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: saving ? '#999999' : '#111111', color: '#F4F1E8', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', marginTop: '8px' });

  return (
    <div data-testid="pages-manager">
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: '700', marginBottom: '8px' }}>
        Pages
      </h1>
      <p style={{ color: '#6B6760', marginBottom: '24px', fontSize: '0.95rem' }}>
        Edit the Home, About the Book and About the Authors page text. (Author names, bios and photos are in the Authors tab; the book cover is in Settings.)
      </p>
      {msg && (
        <div style={{ padding: '12px 16px', marginBottom: '20px', borderRadius: '4px', backgroundColor: msg.includes('success') ? '#e6f7e6' : '#ffe6e6', color: msg.includes('success') ? '#006600' : '#cc0000' }}>
          {msg}
        </div>
      )}

      {/* Home (Landing) page */}
      <div style={card}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '20px' }}>Home (Landing Page)</h2>
        {!home ? <p>Loading...</p> : (
          <>
            <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#9A7B2E', margin: '8px 0 12px' }}>Hero</h3>
            <Field label="Hero title" value={home.hero_title} onChange={(v) => setHome({ ...home, hero_title: v })} />
            <Field label="Hero subtitle" value={home.hero_subtitle} onChange={(v) => setHome({ ...home, hero_subtitle: v })} rows={2} />
            <Field label="Hero tagline (italic line)" value={home.hero_tagline} onChange={(v) => setHome({ ...home, hero_tagline: v })} />

            <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#9A7B2E', margin: '20px 0 12px' }}>"Both teams were right" section</h3>
            <Field label="Heading" value={home.both_title} onChange={(v) => setHome({ ...home, both_title: v })} />
            <ListEditor label="Paragraphs" items={home.both_paragraphs} onChange={(v) => setHome({ ...home, both_paragraphs: v })} rows={3} />

            <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#9A7B2E', margin: '20px 0 12px' }}>Zones section</h3>
            <Field label="Heading" value={home.zones_title} onChange={(v) => setHome({ ...home, zones_title: v })} />
            <Field label="Intro text" value={home.zones_intro} onChange={(v) => setHome({ ...home, zones_intro: v })} rows={3} />
            <ZonesEditor zones={home.zones} onChange={(v) => setHome({ ...home, zones: v })} />

            <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#9A7B2E', margin: '20px 0 12px' }}>"What you'll take from it" section</h3>
            <Field label="Heading" value={home.take_title} onChange={(v) => setHome({ ...home, take_title: v })} />
            <Field label="Text" value={home.take_text} onChange={(v) => setHome({ ...home, take_text: v })} rows={4} />
            <Field label="Link text" value={home.take_link_text} onChange={(v) => setHome({ ...home, take_link_text: v })} />

            <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#9A7B2E', margin: '20px 0 12px' }}>Authors section</h3>
            <Field label="Heading" value={home.authors_title} onChange={(v) => setHome({ ...home, authors_title: v })} />
            <ListEditor label="Author blurbs (1st = left photo, 2nd = right photo)" items={home.author_blurbs} onChange={(v) => setHome({ ...home, author_blurbs: v })} rows={3} />
            <Field label="Closing line" value={home.authors_closing} onChange={(v) => setHome({ ...home, authors_closing: v })} rows={2} />
            <Field label="Link text" value={home.authors_link_text} onChange={(v) => setHome({ ...home, authors_link_text: v })} />

            <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#9A7B2E', margin: '20px 0 12px' }}>"Get the book" section</h3>
            <Field label="Heading" value={home.launch_title} onChange={(v) => setHome({ ...home, launch_title: v })} />
            <Field label="Text" value={home.launch_text} onChange={(v) => setHome({ ...home, launch_text: v })} rows={2} />

            <button data-testid="save-home" onClick={() => save('home', home, setSavingHome)} disabled={savingHome} style={saveBtn(savingHome)}>
              <Save size={18} /> {savingHome ? 'Saving...' : 'Save Home Page'}
            </button>
          </>
        )}
      </div>

      {/* About the Book */}
      <div style={card}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '20px' }}>About the Book</h2>
        {!book ? <p>Loading...</p> : (
          <>
            <Field label="Hero title" value={book.hero_title} onChange={(v) => setBook({ ...book, hero_title: v })} />
            <Field label="Hero subtitle" value={book.hero_subtitle} onChange={(v) => setBook({ ...book, hero_subtitle: v })} rows={2} />
            <Field label="'Why' section heading" value={book.why_title} onChange={(v) => setBook({ ...book, why_title: v })} />
            <Field label="'Why' section text" value={book.why_text} onChange={(v) => setBook({ ...book, why_text: v })} rows={6} />
            <Field label="Key concepts heading" value={book.key_concepts_title} onChange={(v) => setBook({ ...book, key_concepts_title: v })} />
            <ListEditor label="Key concepts" items={book.key_concepts} onChange={(v) => setBook({ ...book, key_concepts: v })} rows={3} />
            <Field label="'Who it's for' heading" value={book.who_title} onChange={(v) => setBook({ ...book, who_title: v })} />
            <ListEditor label="Who this book is for" items={book.who_audiences} onChange={(v) => setBook({ ...book, who_audiences: v })} />
            <button data-testid="save-about-book" onClick={() => save('about_book', book, setSavingBook)} disabled={savingBook} style={saveBtn(savingBook)}>
              <Save size={18} /> {savingBook ? 'Saving...' : 'Save About the Book'}
            </button>
          </>
        )}
      </div>

      {/* About the Authors */}
      <div style={card}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '20px' }}>About the Authors</h2>
        {!authorsPage ? <p>Loading...</p> : (
          <>
            <Field label="Hero title" value={authorsPage.hero_title} onChange={(v) => setAuthorsPage({ ...authorsPage, hero_title: v })} />
            <Field label="Hero subtitle" value={authorsPage.hero_subtitle} onChange={(v) => setAuthorsPage({ ...authorsPage, hero_subtitle: v })} rows={2} />
            <Field label="'Why we wrote it' heading" value={authorsPage.why_title} onChange={(v) => setAuthorsPage({ ...authorsPage, why_title: v })} />
            <ListEditor label="'Why we wrote it' paragraphs" items={authorsPage.why_paragraphs} onChange={(v) => setAuthorsPage({ ...authorsPage, why_paragraphs: v })} rows={4} />
            <button data-testid="save-about-authors" onClick={() => save('about_authors', authorsPage, setSavingAuthors)} disabled={savingAuthors} style={saveBtn(savingAuthors)}>
              <Save size={18} /> {savingAuthors ? 'Saving...' : 'Save About the Authors'}
            </button>
          </>
        )}
      </div>
      {/* Pre-Order (Get the Book) */}
      <div style={card}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '20px' }}>Pre-Order Page</h2>
        <p style={{ color: '#6B6760', marginBottom: '20px', fontSize: '0.9rem' }}>
          Prices, format links and the book cover are managed in the Book Formats and Settings tabs.
        </p>
        {!preorder ? <p>Loading...</p> : (
          <>
            <Field label="Hero heading" value={preorder.hero_title} onChange={(v) => setPreorder({ ...preorder, hero_title: v })} />
            <Field label="Hero text (under heading)" value={preorder.hero_text} onChange={(v) => setPreorder({ ...preorder, hero_text: v })} rows={4} />
            <Field label="Retailer note (under format cards)" value={preorder.retailer_note} onChange={(v) => setPreorder({ ...preorder, retailer_note: v })} rows={2} />
            <button data-testid="save-preorder" onClick={() => save('buy_book', preorder, setSavingPreorder)} disabled={savingPreorder} style={saveBtn(savingPreorder)}>
              <Save size={18} /> {savingPreorder ? 'Saving...' : 'Save Pre-Order Page'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PagesManager;
